/**
 * ManageNamespaces Page
 *
 * Lists user namespaces with admin-aware edit mode, delete support, and
 * navigation to create/edit flows. Surfaces errors and notifications.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth';
import { useNamespace } from '../../context/namespace';
import { del } from '../../api/core';
import { LoadingSpinner } from '../../components/ui/feedback/loading-spinner.jsx';
import ManageNamespaceHeader from '../../components/namespaces/manageNamespaces/ManageNamespaceHeader';
import NamespaceList from '../../components/namespaces/manageNamespaces/NamespaceList';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import Footer from '../../components/shared/Footer';
import SEOHead from '../../components/shared/SEOHead';

export default function ManageNamespaces() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    currentNamespace, 
    namespaces: contextNamespaces,
    isLoading: contextIsLoading,
    error: contextError,
    fetchUserNamespaces
  } = useNamespace();
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setNotification({ type: '', message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const isUserAdmin = (namespace) => {
    if (!user?.email || !namespace?.members) return false;
    
    const userEmail = user.email.toLowerCase();
    const member = namespace.members.find(m => m.email.toLowerCase() === userEmail);
    
    return member?.permission === 'admin';
  };

  const hasAdminAccess = () => {
    return contextNamespaces.some(namespace => isUserAdmin(namespace));
  };

  const handleDelete = async (namespaceId) => {
    try {
      const namespace = contextNamespaces.find(n => n.id === namespaceId);
      const namespaceName = namespace?.name || 'Namespace';
      
      if (!isUserAdmin(namespace)) {
        setNotification({ 
          type: 'error',
          message: 'You need admin privileges to delete this namespace'
        });
        return Promise.reject(new Error('You need admin privileges to delete this namespace'));
      }
      
      if (namespace?.name === currentNamespace) {
        setNotification({ 
          type: 'error', 
          message: 'Cannot delete the currently active namespace. Please switch to a different namespace first.'
        });
        return Promise.reject(new Error('Cannot delete the currently active namespace'));
      }
      
      await del(`/namespaces/${namespaceId}`);
      
      await fetchUserNamespaces(true);
      
      setNotification({ 
        type: 'success',
        message: `"${namespaceName}" has been deleted successfully` 
      });
      
      return Promise.resolve();
    } catch (err) {
      console.error('Error deleting namespace:', err);
      
      let errorMessage = 'Failed to delete namespace';
      
      if (err.message) {
        if (err.message.includes('Cannot delete your default namespace')) {
          errorMessage = 'Cannot delete your default namespace. Please create a new namespace before deleting this one.';
        } else if (err.message.includes('403')) {
          errorMessage = 'You do not have permission to delete this namespace.';
        } else {
          errorMessage = `${errorMessage}: ${err.message}`;
        }
      }
      
      setNotification({ 
        type: 'error',
        message: errorMessage
      });
      
      return Promise.reject(new Error(errorMessage));
    }
  };

  const toggleEditMode = () => {
    if (hasAdminAccess()) {
      setIsEditMode(!isEditMode);
    } else {
      setNotification({ 
        type: 'error', 
        message: 'You need admin privileges to edit namespaces'
      });
    }
  };

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      <SEOHead pageType="app-namespaces" />
      {notification.message && (
        <div className={`mb-4 flex items-center gap-2 rounded-lg px-4 py-3 text-sm ${
          notification.type === 'success' 
            ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
            : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <XCircle className="h-5 w-5 flex-shrink-0" />
          )}
          <span>{notification.message}</span>
          <button 
            onClick={() => setNotification({ type: '', message: '' })}
            className="ml-auto rounded-full p-1 hover:bg-black/5 dark:hover:bg-white/10"
            aria-label="Dismiss"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      <ManageNamespaceHeader
        onNewNamespace={() => navigate('/app/namespaces/create')}
        hasNamespaces={!contextIsLoading && contextNamespaces.length > 0}
        isEditMode={isEditMode}
        onToggleEditMode={toggleEditMode}
        hasAdminNamespaces={hasAdminAccess()}
      />

      {contextIsLoading ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <LoadingSpinner message="Loading namespaces..." />
        </div>
      ) : contextError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/50">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{contextError}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => fetchUserNamespaces(true)}
                  className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <NamespaceList
            namespaces={contextNamespaces}
            onDelete={handleDelete}
            onNamespaceClick={(id) => navigate(`/app/namespaces/edit/${id}`)}
            onNewNamespace={() => navigate('/app/namespaces/create')}
            isEditMode={isEditMode}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}